import { Link } from "@tanstack/react-router";
import {
  Calendar as CalendarIcon,
  CandlestickChart,
  LayoutDashboard,
  LineChart,
  MessageSquare,
  NotebookText,
  PanelLeft,
  PanelRight,
  SlidersHorizontal,
  Upload,
  type LucideIcon,
} from "lucide-react";
import { useState } from "react";

import { useNavPrefetch } from "../../api/useNavPrefetch";
import { cn } from "../../lib/cn";
import { FeedbackDialog } from "../feedback/FeedbackDialog";
import { useTour } from "../onboarding/tourContext";
import { BrandMark } from "./BrandMark";

// Collapsed-state persistence. A dedicated key keeps this independent of the
// Settings schema so the rail remembers the user's choice across reloads.
const SIDEBAR_COLLAPSED_KEY = "sidebarCollapsed";

function readSidebarCollapsed(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "true";
  } catch {
    return false;
  }
}

function writeSidebarCollapsed(value: boolean): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(value));
  } catch {
    // ignore — persistence is best-effort
  }
}

interface NavEntry {
  to: string;
  label: string;
}

// Primary entries carry an icon so they can render in the collapsed icon rail.
interface PrimaryNavEntry extends NavEntry {
  icon: LucideIcon;
  // Matches a TOUR_STEPS target so the product tour can ring this entry.
  tourId?: string;
}

interface SubNavGroup {
  label: string;
  parentTo: string;
  icon: LucideIcon;
  entries: ReadonlyArray<NavEntry>;
}

// Analytics is a single primary destination (ADR 0024 D4): the former
// Analytics dropdown collapsed into one /analytics entry whose six sections
// now render as in-page tabs (see AnalyticsTabs).
const PRIMARY_ENTRIES: ReadonlyArray<PrimaryNavEntry> = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, tourId: "dashboard" },
  { to: "/trades", label: "Trades", icon: CandlestickChart, tourId: "trades" },
  { to: "/calendar", label: "Calendar", icon: CalendarIcon, tourId: "calendar" },
  { to: "/journal/notes", label: "Notes", icon: NotebookText, tourId: "notes" },
  { to: "/analytics", label: "Analytics", icon: LineChart },
];

// Tailwind ring applied to the nav entry the product tour is currently on.
const TOUR_RING =
  "ring-2 ring-primary ring-offset-1 ring-offset-chrome";

// Shared icon-button shape for the collapsed rail.
const RAIL_ICON =
  "flex h-10 w-10 items-center justify-center rounded-control text-on-chrome-variant transition-colors hover:bg-chrome-hover hover:text-on-chrome";

const SETUP_GROUP: SubNavGroup = {
  label: "Setup",
  parentTo: "/accounts",
  icon: SlidersHorizontal,
  entries: [
    { to: "/accounts", label: "Accounts" },
    { to: "/strategies", label: "Strategies" },
    { to: "/instruments", label: "Instruments" },
    { to: "/tags", label: "Tags" },
  ],
};

// Sidebar consumes the `chrome` token namespace so it can be dark (Executive)
// or light (Editorial) without the workspace surface following along.
const ENTRY_BASE =
  "block rounded-control px-3 py-1.5 text-base font-medium text-on-chrome-variant transition-colors hover:bg-chrome-hover hover:text-on-chrome";
// Active entry carries the design's burnt-orange left marker (inset box-shadow)
// in addition to the raised background.
const ENTRY_ACTIVE =
  "bg-chrome-active text-on-chrome shadow-[inset_2px_0_0_var(--color-primary)]";
const SUB_ENTRY_BASE =
  "block rounded-control px-3 py-1 text-xs font-medium text-on-chrome-variant transition-colors hover:bg-chrome-hover hover:text-on-chrome";

type Mode = "simple" | "advanced";

function SubNav({
  group,
  defaultOpen = true,
  badge,
}: {
  group: SubNavGroup;
  defaultOpen?: boolean;
  /** Optional pill displayed next to the group label (e.g. unread alert count). */
  badge?: { count: number; ariaLabel: string };
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={`subnav-${group.label.toLowerCase()}`}
        onClick={() => setOpen((prev) => !prev)}
        className={cn(
          ENTRY_BASE,
          "flex w-full items-center justify-between text-left",
        )}
      >
        <span className="flex items-center gap-2.5">
          <group.icon aria-hidden="true" className="h-4 w-4 shrink-0" />
          <span>{group.label}</span>
          {badge && badge.count > 0 ? (
            <span
              role="status"
              aria-label={badge.ariaLabel}
              data-testid={`subnav-badge-${group.label.toLowerCase()}`}
              className="inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-primary px-1.5 text-[0.625rem] font-medium leading-4 text-on-primary"
            >
              {badge.count > 99 ? "99+" : badge.count}
            </span>
          ) : null}
        </span>
        <span aria-hidden="true" className="text-xs text-on-chrome-muted">
          {open ? "−" : "+"}
        </span>
      </button>
      {open ? (
        <ul
          id={`subnav-${group.label.toLowerCase()}`}
          className="mt-1 ml-3 space-y-0.5 border-l border-chrome-border pl-2"
        >
          {group.entries.map((entry) => (
            <li key={entry.to}>
              <Link
                to={entry.to}
                className={SUB_ENTRY_BASE}
                activeProps={{ className: ENTRY_ACTIVE }}
              >
                {entry.label}
              </Link>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

export function Sidebar() {
  const [mode, setMode] = useState<Mode>("simple");
  const [collapsed, setCollapsed] = useState<boolean>(readSidebarCollapsed);
  const { activeTargetId } = useTour();
  const prefetch = useNavPrefetch();

  function toggleCollapsed() {
    setCollapsed((prev) => {
      const next = !prev;
      writeSidebarCollapsed(next);
      return next;
    });
  }

  return (
    <nav
      aria-label="Primary"
      className={cn(
        "flex h-screen shrink-0 flex-col overflow-hidden border-r border-chrome-border bg-chrome text-on-chrome transition-[width] duration-200 ease-in-out",
        collapsed ? "w-16" : "w-60",
      )}
    >
      <div
        className={cn(
          "flex items-center border-b border-chrome-border px-4 py-4",
          collapsed ? "justify-center" : "justify-between",
        )}
      >
        {collapsed ? null : (
          <div className="flex min-w-0 items-center gap-2.5">
            <BrandMark size={32} />
            <span className="truncate font-sans text-2xl font-bold tracking-tight text-on-chrome">
              WickScope
            </span>
          </div>
        )}
        <button
          type="button"
          onClick={toggleCollapsed}
          aria-expanded={!collapsed}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-control text-on-chrome-variant transition-colors hover:bg-chrome-hover hover:text-on-chrome"
        >
          {collapsed ? (
            <PanelRight aria-hidden="true" className="h-5 w-5" />
          ) : (
            <PanelLeft aria-hidden="true" className="h-5 w-5" />
          )}
        </button>
      </div>

      {collapsed ? (
        <div className="flex flex-1 flex-col items-center gap-1 overflow-y-auto py-3">
          {PRIMARY_ENTRIES.map((entry) => (
            <Link
              key={entry.to}
              to={entry.to}
              aria-label={entry.label}
              title={entry.label}
              data-tour-id={entry.tourId}
              onPointerEnter={() => prefetch(entry.to)}
              onFocus={() => prefetch(entry.to)}
              className={cn(
                RAIL_ICON,
                entry.tourId !== undefined &&
                  activeTargetId === entry.tourId &&
                  TOUR_RING,
              )}
              activeProps={{ className: ENTRY_ACTIVE }}
            >
              <entry.icon aria-hidden="true" className="h-5 w-5" />
            </Link>
          ))}
          <div className="mt-auto flex flex-col items-center gap-1">
            <FeedbackDialog
              trigger={
                <button
                  type="button"
                  aria-label="Send feedback"
                  title="Help us Improve"
                  className={RAIL_ICON}
                >
                  <MessageSquare aria-hidden="true" className="h-5 w-5" />
                </button>
              }
            />
            <Link
              to="/imports/new"
              aria-label="Import"
              title="Import"
              data-tour-id="import"
              className={cn(
                RAIL_ICON,
                "text-on-chrome",
                activeTargetId === "import" && TOUR_RING,
              )}
            >
              <Upload aria-hidden="true" className="h-5 w-5" />
            </Link>
          </div>
        </div>
      ) : (
        <>
      <div className="border-b border-chrome-border px-4 py-3">
        <div
          role="radiogroup"
          aria-label="Sidebar mode"
          className="flex w-full rounded-control border border-chrome-border bg-chrome-hover p-1"
        >
          {(["simple", "advanced"] as const).map((value) => (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={mode === value}
              onClick={() => setMode(value)}
              className={cn(
                "flex-1 rounded-sm px-3 py-1.5 text-sm font-semibold capitalize transition-colors",
                mode === value
                  ? "bg-chrome-active text-on-chrome shadow-sm"
                  : "text-on-chrome-variant hover:text-on-chrome",
              )}
            >
              {value}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto py-3 pl-5 pr-3">
        <ul className="space-y-0.5">
          {PRIMARY_ENTRIES.map((entry) => (
            <li key={entry.to}>
              <Link
                to={entry.to}
                data-tour-id={entry.tourId}
                onPointerEnter={() => prefetch(entry.to)}
                onFocus={() => prefetch(entry.to)}
                className={cn(
                  ENTRY_BASE,
                  "flex items-center gap-2.5",
                  entry.tourId !== undefined &&
                    activeTargetId === entry.tourId &&
                    TOUR_RING,
                )}
                activeProps={{ className: ENTRY_ACTIVE }}
              >
                <entry.icon aria-hidden="true" className="h-4 w-4 shrink-0" />
                <span>{entry.label}</span>
              </Link>
            </li>
          ))}
          {mode === "advanced" ? (
            <li>
              <SubNav group={SETUP_GROUP} defaultOpen={false} />
            </li>
          ) : null}
        </ul>
      </div>

      <div className="mb-6 space-y-2 border-t border-chrome-border px-3 py-3">
        <FeedbackDialog
          trigger={
            <button
              type="button"
              className={cn(ENTRY_BASE, "flex w-full items-center gap-2.5")}
            >
              <MessageSquare aria-hidden="true" className="h-4 w-4 shrink-0" />
              <span>Help us Improve</span>
            </button>
          }
        />
        <Link
          to="/imports/new"
          data-tour-id="import"
          className={cn(
            "flex w-full items-center justify-center gap-2 rounded-control bg-primary px-3 py-1.5 text-base font-medium text-on-primary transition-colors hover:bg-primary-hover",
            activeTargetId === "import" && TOUR_RING,
          )}
        >
          <Upload aria-hidden="true" className="h-4 w-4 shrink-0" />
          <span>Import</span>
        </Link>
      </div>
        </>
      )}
    </nav>
  );
}
